import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/http';

type Mode = 'login' | 'register' | 'forgot';

interface Props {
  /** Onglets connexion / création. `false` pour la console : pas d'inscription ici. */
  allowRegister?: boolean;
  /** Fond sombre (console) ou clair (application conducteur). */
  tone?: 'light' | 'dark';
  onSuccess?: () => void;
}

/**
 * Formulaire d'identification — email + mot de passe, micro-API PHP.
 *
 * Remplace les composants du fournisseur d'identité tiers. Ce qui reste côté
 * navigateur : deux champs et un appel HTTP. Ce qui n'y est jamais : le rôle
 * (attribué par la base), le hachage du mot de passe, la moindre requête SQL.
 *
 * L'API renvoie des messages déjà lisibles (`{ error }`), on les affiche tels
 * quels au lieu de réinventer une table de traduction des codes.
 */
export default function AuthForm({ allowRegister = true, tone = 'light', onSuccess }: Props) {
  const { signIn, signUp, forgotPassword } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const dark = tone === 'dark';
  const card = dark ? 'bg-gray-900 border border-gray-800' : 'bg-white';
  const label = dark ? 'text-gray-400' : 'text-gray-500';
  const field = dark
    ? 'bg-gray-950 border-gray-800 text-white placeholder-gray-600'
    : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
        onSuccess?.();
      } else if (mode === 'register') {
        await signUp(email, password, fullName);
        onSuccess?.();
      } else {
        await forgotPassword(email);
        setNotice('Si un compte existe pour cette adresse, un lien de réinitialisation vient d\u2019être envoyé. Il expire dans une heure.');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Connexion impossible, réessayez');
    } finally {
      setBusy(false);
    }
  }

  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
    setNotice(null);
    setPassword('');
  }

  return (
    <div className={`w-full max-w-sm rounded-3xl shadow-xl p-5 ${card}`}>
      {allowRegister && mode !== 'forgot' && (
        <div className={`grid grid-cols-2 gap-1 rounded-xl p-1 mb-5 ${dark ? 'bg-gray-950' : 'bg-gray-100'}`}>
          {([['login', 'Connexion'], ['register', 'Créer un compte']] as const).map(([id, text]) => (
            <button
              key={id}
              type="button"
              onClick={() => switchTo(id)}
              className={`py-2 rounded-lg text-xs font-bold transition-colors ${
                mode === id
                  ? dark ? 'bg-gray-800 text-white' : 'bg-white text-blue-600 shadow-sm'
                  : dark ? 'text-gray-500' : 'text-gray-500'
              }`}
            >
              {text}
            </button>
          ))}
        </div>
      )}

      {mode === 'forgot' && (
        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={() => switchTo('login')}
            className={`w-8 h-8 rounded-xl flex items-center justify-center ${dark ? 'bg-gray-800' : 'bg-gray-100'}`}
            aria-label="Retour à la connexion"
          >
            <i className={`ri-arrow-left-line text-sm ${dark ? 'text-gray-300' : 'text-gray-500'}`}></i>
          </button>
          <h2 className={`font-bold text-sm ${dark ? 'text-white' : 'text-gray-800'}`}>Mot de passe oublié</h2>
        </div>
      )}

      <form onSubmit={submit} className="space-y-3.5">
        {mode === 'register' && (
          <div>
            <label htmlFor="af-name" className={`block text-[11px] font-bold uppercase tracking-wide mb-1.5 ${label}`}>
              Nom complet
            </label>
            <input
              id="af-name"
              type="text"
              autoComplete="name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jean Dupont"
              className={`w-full min-h-[48px] px-4 rounded-2xl border text-sm outline-none focus:border-blue-500 ${field}`}
            />
          </div>
        )}

        <div>
          <label htmlFor="af-email" className={`block text-[11px] font-bold uppercase tracking-wide mb-1.5 ${label}`}>
            Adresse email
          </label>
          <input
            id="af-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@societe.be"
            className={`w-full min-h-[48px] px-4 rounded-2xl border text-sm outline-none focus:border-blue-500 ${field}`}
          />
        </div>

        {mode !== 'forgot' && (
          <div>
            <label htmlFor="af-pass" className={`block text-[11px] font-bold uppercase tracking-wide mb-1.5 ${label}`}>
              Mot de passe
            </label>
            <div className="relative">
              <input
                id="af-pass"
                type={show ? 'text' : 'password'}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className={`w-full min-h-[48px] pl-4 pr-12 rounded-2xl border text-sm outline-none focus:border-blue-500 ${field}`}
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center"
                aria-label={show ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                <i className={`${show ? 'ri-eye-off-line' : 'ri-eye-line'} text-base ${dark ? 'text-gray-500' : 'text-gray-400'}`}></i>
              </button>
            </div>
            {mode === 'register' && (
              <p className={`text-[10px] mt-1.5 leading-relaxed ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                10 caractères minimum, avec une minuscule, une majuscule et un chiffre.
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
            <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
            <p className="text-xs text-red-600 font-medium leading-snug">{error}</p>
          </div>
        )}

        {notice && (
          <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <i className="ri-mail-check-line text-emerald-600 text-sm flex-shrink-0 mt-0.5"></i>
            <p className="text-xs text-emerald-700 font-medium leading-snug">{notice}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full min-h-[52px] rounded-2xl bg-blue-600 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy && <i className="ri-loader-4-line animate-spin text-base"></i>}
          {mode === 'login' ? 'Se connecter' : mode === 'register' ? 'Créer mon compte' : 'Envoyer le lien'}
        </button>
      </form>

      {mode === 'login' && (
        <button
          type="button"
          onClick={() => switchTo('forgot')}
          className={`w-full min-h-[44px] text-xs font-semibold mt-1 ${dark ? 'text-blue-300' : 'text-blue-600'}`}
        >
          Mot de passe oublié ?
        </button>
      )}
    </div>
  );
}
